import React, {useEffect, useMemo, useState} from 'react';
import {EventDetailDto} from '../../generated/models/EventDetailDto';
import {InsertEventDto} from '../../generated/models/InsertEventDto';
import {formStyle} from './formStyle';
import {FormField} from './formField';
import {MessageCard} from '../card/messageCard';
import {ImageUploader} from '../image/ImageUploader';
import {getSasUrl, uploadToBlob} from '../../common/services/azureBlobUploader';
import {useUiStore} from '../../common/stores/useUiStore';
import {FormScreenTrigger} from './FormScreenTrigger';
import {useTagsStore} from '../../common/stores/useTagsStore';
import {DashDropdown, OnDropdownChangeInput} from '../dropdowns/DashDropdown';
import {useQueryLocationTags} from '../../common/hooks/useLocationTags';
import {FormDatePicker} from './FormDatePicker';
import {FillPayload} from '../button/FormFillerButton';
import {RecurrenceSection, Occurrence} from './RecurrenceSection';
import {TagSelectorModal} from '../tags/TagSelectorModal';

type EventFomProps = React.FC<{
  type: 'CREATE' | 'EDIT';
  onSubmit: (data: InsertEventDto[]) => void;
  eventData?: EventDetailDto | null;
  fillPayload?: FillPayload | null;
}>;

const getNumber = (value: string | number): number | null => {
  let result = Number(value);
  if (isNaN(result)) {
    return null;
  }
  return result;
};

export const EventForm: EventFomProps = ({
  onSubmit,
  eventData,
  type,
  fillPayload,
}) => {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [images, setImages] = useState<(string | File)[]>([]);
  const [formData, setFormData] = useState<InsertEventDto | null>(null);
  const setIsLoading = useUiStore(state => state.setIsLoading);
  const {data: locations} = useQueryLocationTags();
  const {selectedTagCodes, setSelectedTagCodes} = useTagsStore();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  // null → recurrence disabled; [] → enabled but not generated; [...] → generated
  const [occurrences, setOccurrences] = useState<Occurrence[] | null>(null);
  const [fillOccurrences, setFillOccurrences] = useState<
    Occurrence[] | undefined
  >(undefined);

  const initialOccurrences = useMemo(() => {
    if (fillOccurrences && fillOccurrences.length > 0) return fillOccurrences;
    if (
      type === 'EDIT' &&
      eventData &&
      eventData.occurrences &&
      eventData.occurrences.length > 1
    ) {
      return eventData.occurrences.map((o, i) => ({
        id: `occ-existing-${i}`,
        startDateTime: new Date(o.dateTimeStart),
        endDateTime: new Date(o.dateTimeStop),
      }));
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData?.occurrences?.length, fillOccurrences]);

  useEffect(() => {
    if (eventData) {
      setFormData({
        name: eventData.name,
        description: eventData.description,
        locationCode: eventData.locationCode,
        coordinates: eventData.coordinates,
        address: eventData.address,
        tagCodes: eventData.tagCodes,
        mapImageCdnPaths: eventData.mapImageCdnPaths,
        startDateTime: eventData.occurrences?.[0]?.dateTimeStart,
        endDateTime: eventData.occurrences?.[0]?.dateTimeStop,
      });
      setImages(eventData.mapImageCdnPaths || []);
    }
  }, [eventData]);

  useEffect(() => {
    if (!fillPayload) return;
    const cityOption = fillPayload.city
      ? locations?.find(
          l => l.name.toLowerCase() === fillPayload.city!.toLowerCase(),
        )
      : undefined;
    setFormData(prev => {
      const current = prev || ({} as InsertEventDto);
      return {
        ...current,
        ...(fillPayload.title !== undefined && {name: fillPayload.title}),
        ...(fillPayload.description !== undefined && {
          description: fillPayload.description,
        }),
        ...(fillPayload.startDate !== undefined && {
          startDateTime: new Date(fillPayload.startDate),
        }),
        ...(fillPayload.endDate !== undefined && {
          endDateTime: new Date(fillPayload.endDate),
        }),
        ...(cityOption && {locationCode: cityOption.code}),
        address: {
          ...(current.address || {}),
          ...(cityOption && {cityName: cityOption.name}),
          ...(fillPayload.streetName !== undefined && {
            streetName: fillPayload.streetName,
          }),
          ...(fillPayload.streetNumber !== undefined && {
            streetNumber: fillPayload.streetNumber,
          }),
        },
        coordinates: {
          ...(current.coordinates || {}),
          ...(fillPayload.latitude !== undefined && {
            latitude: fillPayload.latitude,
          }),
          ...(fillPayload.longitude !== undefined && {
            longitude: fillPayload.longitude,
          }),
        },
      } as InsertEventDto;
    });

    if (fillPayload.occurrences && fillPayload.occurrences.length > 0) {
      const mapped: Occurrence[] = fillPayload.occurrences.map((o, i) => ({
        id: `occ-fill-${Date.now()}-${i}`,
        startDateTime: new Date(o.startDate),
        endDateTime: new Date(o.endDate),
      }));
      setOccurrences(mapped);
      setFillOccurrences(mapped);
    }
  }, [fillPayload]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTagSelectionOnClick = () => {
    setIsTagModalOpen(true);
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const {name, value} = e.target;

    setFormData(prev => {
      const current = prev || ({} as InsertEventDto);

      if (name === 'latitude' || name === 'longitude') {
        return {
          ...current,
          coordinates: {
            ...(current.coordinates || {}),
            [name]: value,
          },
        } as InsertEventDto;
      }

      if (
        name === 'cityName' ||
        name === 'streetName' ||
        name === 'streetNumber' ||
        name === 'placeName' ||
        name === 'country'
      ) {
        return {
          ...current,
          address: {
            ...(current.address || {}),
            [name]: value,
          },
        } as InsertEventDto;
      }
      return {
        ...current,

        [name]: value,
      } as InsertEventDto;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);

    if (!formData?.name || !formData?.description) {
      setFieldError('The title and description are required.');
      return;
    }

    const latitude = getNumber(formData.coordinates.latitude);
    const longitude = getNumber(formData.coordinates.longitude);
    if (latitude === null) {
      setFieldError('Latitude is not a valid number');
      return;
    }
    if (longitude === null) {
      setFieldError('Longitude is not a valid number');
      return;
    }

    // TODO make validation
    // Validation for tags
    // if (selectedTags.length === 0) {
    //   setShowTagError(true);
    //   setFieldError('Please select at least one tag');
    //   return;
    // }

    const start = new Date(formData?.startDateTime ?? '');
    const stop = new Date(formData?.endDateTime ?? '');

    if (isNaN(start.getTime()) || isNaN(stop.getTime())) {
      setFieldError('Select a start and an end date.');
      return;
    }

    if (stop.getTime() <= start.getTime()) {
      setFieldError('End date must be after Start date');
      return;
    }

    if (occurrences !== null && occurrences.length === 0) {
      setFieldError(
        'Click "Generate" to create weekly occurrences before submitting.',
      );
      return;
    }

    setIsLoading(true);
    const sasUrl = await getSasUrl();
    if (!sasUrl) {
      console.error('Was unable to Connect to Azure');
      setFieldError('Was unable to Connect to Azure');
      setIsLoading(false);
      return;
    }

    if (images.length === 0) {
      setFieldError('No image was selected');
      setIsLoading(false);
      return;
    }

    const urls: string[] = [];

    // A failed upload throws; without this the full screen loader never closes.
    try {
      for (let i = 0; i < images.length; i++) {
        if (typeof images[i] != 'string') {
          const url = await uploadToBlob({
            sasUrl,
            file: images[i] as File,
            maxWidth: 1920,
            maxHeight: 1080,
            quality: 100,
          });
          urls.push(url);
        } else if (typeof images[i] === 'string') {
          urls.push(images[i] as string);
        }
      }
    } catch (uploadError) {
      console.error('Image upload failed', uploadError);
      setFieldError('An image could not be uploaded. Try again.');
      setIsLoading(false);
      return;
    }

    const submitData = {
      ...formData,
      name: formData?.name || '',
      description: formData?.description || '',
      mapImageCdnPaths: urls,
      address: {
        placeName: formData?.address?.placeName ?? '',
        cityName: formData?.address?.cityName ?? '',
        streetName: formData?.address?.streetName ?? '',
        country: formData?.address?.country ?? 'Romania',
        streetNumber: formData?.address?.streetNumber ?? '',
      },
      // Because in form this is a string we need to set the numbers
      coordinates: {
        latitude,
        longitude,
      },
    } as InsertEventDto;
    if (occurrences !== null && occurrences.length > 0) {
      onSubmit(
        occurrences.map(occ => ({
          ...submitData,
          startDateTime: occ.startDateTime,
          endDateTime: occ.endDateTime,
        })),
      );
    } else {
      onSubmit([submitData]);
    }
  };

  // Load existing images when editing
  // useEffect(() => {
  //   if (eventData?.mapImageCdnPaths) {
  //     setImages(eventData.mapImageCdnPaths);
  //   }
  // }, [eventData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setImages(prev => {
        const combined = [...prev, ...newFiles];
        return combined.slice(0, 10); // Limit to 10 total
      });
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleOnDropdownChange = ({code, name}: OnDropdownChangeInput) => {
    setFormData({
      ...(formData || ({} as any)),
      locationCode: code,
      address: {...formData?.address, cityName: name},
    });
  };

  const selectedTagsText = selectedTagCodes?.length
    ? `${selectedTagCodes?.length} Tags selected`
    : 'Open Tag Selector';

  return (
    <>
      <form onSubmit={handleSubmit} style={formStyle}>
        <FormField
          label="Title"
          name="name"
          required
          placeholder="title"
          value={formData?.name}
          onChange={onChange}
        />

        <FormField
          label="Description"
          name="description"
          type="textarea"
          required
          placeholder="description"
          value={formData?.description ?? ''}
          onChange={onChange}
        />

        {type === 'EDIT' &&
          eventData &&
          (eventData.occurrences?.length ?? 0) === 0 && (
            <p style={endedHintStyle}>
              This event has already ended, so it has no dates left. Pick new
              dates to schedule it again.
            </p>
          )}

        <FormDatePicker
          label="Start Date"
          required
          disabled={occurrences !== null}
          selectedDate={formData?.startDateTime || null}
          onChange={date =>
            setFormData({
              ...formData!,
              startDateTime: date!,
            })
          }
        />

        <FormDatePicker
          label="End Date"
          required
          disabled={occurrences !== null}
          selectedDate={formData?.endDateTime || null}
          onChange={date =>
            setFormData({
              ...formData!,
              endDateTime: date!,
            })
          }
        />

        <RecurrenceSection
          startDate={formData?.startDateTime || null}
          endDate={formData?.endDateTime || null}
          initialOccurrences={initialOccurrences}
          disabled={type === 'EDIT'}
          onChange={setOccurrences}
        />

        <FormField
          label="Place Name"
          name="placeName"
          placeholder="eg: Parcul Central, Vivo Mall, .."
          value={formData?.address?.placeName ?? ''}
          onChange={onChange}
        />

        <div style={{marginBottom: '20px'}}>
          <label style={labelStyle}>City Name</label>
          <DashDropdown
            options={locations || []}
            selectedValue={formData?.locationCode}
            onChange={handleOnDropdownChange}
            // required
          />
        </div>

        <FormField
          label="Street Name"
          name="streetName"
          required
          placeholder="Street Name"
          value={formData?.address?.streetName ?? ''}
          onChange={onChange}
        />

        <FormField
          label="Street Number"
          name="streetNumber"
          required
          placeholder="Street Number"
          value={formData?.address?.streetNumber ?? ''}
          onChange={onChange}
        />

        <FormField
          label="Latitude"
          name="latitude"
          required
          placeholder="Latitude"
          value={`${formData?.coordinates?.latitude || ''}`}
          onChange={onChange}
        />

        <FormField
          label="longitude"
          name="longitude"
          required
          placeholder="Longitude"
          value={`${formData?.coordinates?.longitude || ''}`}
          onChange={onChange}
        />

        <FormScreenTrigger
          label="Tags"
          buttonText={selectedTagsText}
          required
          // error={tagValidationError}
          onClick={handleTagSelectionOnClick}
        />

        <ImageUploader images={images} onChange={setImages} maxImages={10} />

        <button
          type="submit"
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#daff00',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'transform 0.15s, opacity 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.03)';
            e.currentTarget.style.opacity = '0.9';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.opacity = '1';
          }}>
          {type === 'EDIT' ? 'Update Event' : 'Create Event'}
        </button>
        <MessageCard type={fieldError ? 'error' : null} message={fieldError} />
      </form>
      {isTagModalOpen && (
        <TagSelectorModal
          selectedTagCodes={selectedTagCodes ?? []}
          onApply={setSelectedTagCodes}
          onClose={() => setIsTagModalOpen(false)}
        />
      )}
    </>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#aaa', // Soft gray for non-distracting hierarchy
  fontSize: '0.9rem',
  fontWeight: 600,
  textTransform: 'uppercase', // All caps for a professional "dashboard" look
  letterSpacing: '0.5px',
  marginBottom: '6px', // Spacing between the label and the dropdown
};

const endedHintStyle: React.CSSProperties = {
  color: '#daff00',
  fontSize: '0.85rem',
  margin: '0 0 16px',
};
